/**
 * Frontend Analytics utility
 * Provides a unified interface for client-side analytics (Mixpanel, OpenPanel, PostHog, etc.)
 */

import { useOpenPanel } from "@openpanel/nextjs";
import mixpanel from "mixpanel-browser";
import analyticsConfig from "./analyticsConfig";
import { getPostHog, initPostHog, PLATFORM_SUPER_PROPERTY } from "./posthogClient";

// Simple mixpanel initialization
try {
  const shouldMixpanelTrackPageViews = analyticsConfig.pageViewProviders.includes("mixpanel");

  mixpanel.init(process.env.NEXT_PUBLIC_MIXPANEL_TOKEN || "5514b21b26f1affed9919eee0d393391", {
    debug: false,
    track_pageview: shouldMixpanelTrackPageViews,
    persistence: "localStorage",
  });
} catch (error) {
  console.error(`Failed to initialize Mixpanel: ${error.message}`);
  mixpanel = {
    track: () => {},
    identify: () => {},
    people: { set: () => {} },
  };
}

/**
 * Resolve which providers should receive a given event.
 *
 * analyticsConfig.events is a per-event override map. It had been declared but
 * never read, so the three events listed there were silently falling back to
 * defaultProviders; it is honoured now.
 *
 * @param {string} eventName
 * @returns {string[]} provider names
 */
const providersFor = (eventName) =>
  analyticsConfig.events?.[eventName] || analyticsConfig.defaultProviders;

/**
 * Track an event in analytics platforms
 * @param {string} eventName - Name of the event to track
 * @param {Object} properties - Properties/data associated with the event
 */
const trackEvent = (eventName, properties = {}) => {
  if (!eventName) {
    console.warn("Analytics: Missing event name in trackEvent call");
    return;
  }

  const providers = providersFor(eventName);

  if (providers.includes("mixpanel")) {
    try {
      mixpanel.track(eventName, properties);
    } catch (error) {
      console.error(`Mixpanel ERROR for ${eventName}: ${error.message}`);
    }
  }

  // Track in OpenPanel if configured
  if (providers.includes("openpanel")) {
    const op = useOpenPanel();
    try {
      op.track(eventName, properties);
    } catch (error) {
      console.error(`OpenPanel ERROR for ${eventName}: ${error.message}`);
    }
  }

  // Track in PostHog if configured
  if (providers.includes("posthog")) {
    try {
      const ph = getPostHog();
      if (ph) {
        ph.capture(eventName, properties);
      }
    } catch (error) {
      console.error(`PostHog ERROR for ${eventName}: ${error.message}`);
    }
  }
};

/**
 * Identify a user in analytics platforms
 * @param {string} userId - Unique identifier for the user
 * @param {Object} traits - User traits/properties
 */
const identifyUser = (userId, traits = {}) => {
  if (!userId) {
    console.warn("Analytics: Missing userId in identifyUser call");
    return;
  }

  const providers = analyticsConfig.userIdentificationProviders;

  if (providers.includes("mixpanel")) {
    try {
      mixpanel.identify(userId);
      if (Object.keys(traits).length > 0) {
        mixpanel.people.set(traits);
      }
    } catch (error) {
      console.error(`Failed to identify user "${userId}" in Mixpanel:`, error);
    }
  }

  // Identify in OpenPanel if configured
  if (providers.includes("openpanel")) {
    const op = useOpenPanel();
    try {
      op.identify({
        profileId: userId,
        ...traits,
      });
    } catch (error) {
      console.error(`Failed to identify user "${userId}" in OpenPanel:`, error);
    }
  }

  // Identify in PostHog if configured. This is also what promotes an anonymous
  // visitor into a person profile, since the client runs in identified_only mode.
  if (providers.includes("posthog")) {
    try {
      const ph = getPostHog();
      if (ph) {
        ph.identify(String(userId), traits);
      }
    } catch (error) {
      console.error(`Failed to identify user "${userId}" in PostHog:`, error);
    }
  }
};

/**
 * Identify a buyer at order confirmation, resetting first only if this device
 * was already identified as a *different* buyer (a shared device / kiosk).
 * A brand-new anonymous session must NOT be reset here: resetting it would
 * mint a fresh anon id and sever the pre-purchase funnel (product_viewed,
 * checkout_payment_started, ...) from the person profile this call creates.
 * @param {string} userId
 * @param {Object} traits
 */
const identifyBuyer = (userId, traits = {}) => {
  if (!userId) {
    console.warn("Analytics: Missing userId in identifyBuyer call");
    return;
  }

  if (analyticsConfig.userIdentificationProviders.includes("posthog")) {
    try {
      const ph = getPostHog();
      if (ph) {
        const currentDistinctId = ph.get_distinct_id();
        const deviceId = ph.get_property("$device_id");
        const alreadyIdentifiedAsSomeoneElse =
          currentDistinctId &&
          currentDistinctId !== deviceId &&
          currentDistinctId !== String(userId);
        if (alreadyIdentifiedAsSomeoneElse) {
          resetUser();
        }
      }
    } catch (error) {
      console.error(
        `Failed to check existing PostHog identity before identifying "${userId}":`,
        error,
      );
    }
  }

  identifyUser(userId, traits);
};

/**
 * Record a page view. Mixpanel and OpenPanel can do this themselves via their
 * init options; PostHog is configured with capture_pageview: false because the
 * pages-router SPA changes routes without a reload, so it is driven from here.
 * @param {string} path - Path that was viewed
 * @param {Object} properties - Extra properties for the view
 */
const trackPageView = (path, properties = {}) => {
  const providers = analyticsConfig.pageViewProviders;

  if (providers.includes("posthog")) {
    try {
      const ph = getPostHog();
      if (ph) {
        ph.capture("$pageview", {
          $current_url: typeof window !== "undefined" ? window.location.href : path,
          path,
          ...properties,
        });
      }
    } catch (error) {
      console.error(`PostHog ERROR for pageview ${path}: ${error.message}`);
    }
  }
};

// The last properties passed to registerSuperProperties. PostHog's reset()
// clears its whole persistence layer, super properties included, so resetUser
// re-applies these afterwards — otherwise every event on this page would lose
// store_id the moment a buyer's identity is reset.
let lastSuperProperties = {};

/**
 * Attach properties to every subsequent event from this browser (PostHog only).
 * Used for store context, so every storefront event carries the store it came from.
 * @param {Object} properties
 */
const registerSuperProperties = (properties = {}) => {
  lastSuperProperties = { ...lastSuperProperties, ...properties };
  if (!analyticsConfig.defaultProviders.includes("posthog")) return;
  try {
    const ph = getPostHog();
    if (ph) {
      ph.register(properties);
    }
  } catch (error) {
    console.error(`Failed to register PostHog super properties: ${error.message}`);
  }
};

/**
 * Clear the identified person and start a fresh anonymous id. Call at an
 * identity boundary (e.g. a new buyer identified on a shared device),
 * otherwise the next user on the device inherits the previous one's profile.
 */
const resetUser = () => {
  try {
    const ph = getPostHog();
    if (ph) {
      ph.reset();
      // reset() clears the whole persistence layer, including the `platform`
      // super property registered once at init (utils/posthogClient.js) —
      // without this it silently vanishes from every event for the rest of
      // the session.
      ph.register({ ...PLATFORM_SUPER_PROPERTY, ...lastSuperProperties });
    }
  } catch (error) {
    console.error(`Failed to reset PostHog user: ${error.message}`);
  }

  try {
    if (typeof mixpanel.reset === "function") {
      mixpanel.reset();
    }
  } catch (error) {
    console.error(`Failed to reset Mixpanel user: ${error.message}`);
  }
};

/**
 * Check if Mixpanel analytics is properly initialized
 * @returns {boolean} Whether Mixpanel is initialized
 */
const isMixpanelAvailable = () => !!mixpanel && typeof mixpanel.track === "function";

module.exports = {
  trackEvent,
  identifyUser,
  identifyBuyer,
  trackPageView,
  registerSuperProperties,
  resetUser,
  initPostHog,
  isMixpanelAvailable,
};
